// Spring presets — innernet's motion vocabulary, tuned to the house feel:
// soft, drifting, settle-in. NEVER bouncy. The numbers here are the single
// source of truth for "how innernet moves"; components reference them by name
// (gentle / drift / settle / snappy) instead of hand-tuning stiffness inline.
//
// The values are chosen to echo the CSS easing language already in
// globals.css (`--ease-soft: cubic-bezier(0.22, 1, 0.36, 1)` and
// `--ease-drift: cubic-bezier(0.18, 0.72, 0.22, 1)`): a critically-to-slightly
// over-damped spring with no visible overshoot, so spring motion sits next to
// the existing CSS without a jarring change in character.
//
// All presets keep `bounce` implicit-zero by pairing stiffness with enough
// damping that the response is monotonic (no oscillation). If you ever want a
// playful overshoot, add a NEW named preset — do not loosen these.

import type { SpringOptions } from "framer-motion";

/**
 * The named springs. Lower stiffness = heavier, more inertial; higher damping
 * = settles sooner with no wobble. `restDelta`/`restSpeed` are loosened a touch
 * from the defaults so springs that drive cheap scalars (e.g. the organism age,
 * 0..1) come to rest promptly instead of trickling sub-pixel updates forever.
 */
export const SPRINGS = {
  /**
   * gentle — the default. Soft, weighted, no overshoot. Use for most
   * pointer/hover/entrance work where the motion should feel calm and present.
   */
  gentle: { stiffness: 170, damping: 26, mass: 1, restDelta: 0.001, restSpeed: 0.01 },

  /**
   * drift — heavier and slower; reads as inertia. The scroll→organism-age
   * signal uses this: the value visibly *weighs* and lags the raw input, then
   * settles. The hallmark "doesn't register every pixel of scroll" feel.
   */
  drift: { stiffness: 90, damping: 24, mass: 1.1, restDelta: 0.0005, restSpeed: 0.005 },

  /**
   * settle — between gentle and drift; for panel/list entrances that should
   * arrive with a little weight but not linger.
   */
  settle: { stiffness: 210, damping: 30, mass: 1, restDelta: 0.001, restSpeed: 0.01 },

  /**
   * snappy — quick and responsive, still over-damped (no bounce). For press
   * feedback and small affordances that should feel immediate but soft.
   */
  snappy: { stiffness: 380, damping: 34, mass: 0.9, restDelta: 0.001, restSpeed: 0.01 },

  /**
   * layout — FLIP / reorder moves. Stiff + heavily damped so an item flies to
   * its new slot quickly and arrives dead-still (no settle wobble on a list).
   */
  layout: { stiffness: 500, damping: 40, mass: 1, restDelta: 0.001, restSpeed: 0.01 },

  /**
   * pop — the ONE sanctioned overshoot (the header's "add a NEW preset" escape
   * hatch). Damping is intentionally just below critical for a single small
   * bounce. Reserved for the memory-flow dot + the sympathetic icon pulse —
   * do NOT reuse it for general UI, or the house "never bouncy" feel erodes.
   */
  pop: { stiffness: 480, damping: 22, mass: 0.9, restDelta: 0.001, restSpeed: 0.01 },
} as const satisfies Record<string, SpringOptions>;

export type SpringName = keyof typeof SPRINGS;

/** Resolve a preset name (or a raw SpringOptions object) to SpringOptions. */
export function spring(name: SpringName | SpringOptions): SpringOptions {
  return typeof name === "string" ? SPRINGS[name] : name;
}

/**
 * A `type: "spring"` Transition built from a preset — for use directly in
 * `transition={...}` on a `motion.*` element / variant.
 */
export function springTransition(name: SpringName | SpringOptions = "gentle") {
  return { type: "spring" as const, ...spring(name) };
}

/**
 * Instant (zero-duration) transition — the reduced-motion fallback for any
 * spring. `motion.*` elements snap straight to their target.
 */
export const INSTANT = { duration: 0 } as const;
